import { useState } from "react";
import { EstimateResult } from "@/shared/hooks/useAssetEstimate";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { AnimatedShinyText } from "@/shared/components/ui/animated-shiny-text";
import { Pencil, Info } from "lucide-react";
import { currencyInput } from "@/shared/utils/currencyInput";

interface EstimateResultCardProps {
  result: EstimateResult;
  assetType: "real-estate" | "vehicle";
  onConfirm: (value: number, itemType?: string) => void;
  onCancel: () => void;
}

export function EstimateResultCard({
  result,
  assetType,
  onConfirm,
  onCancel,
}: EstimateResultCardProps) {
  const [editedValue, setEditedValue] = useState(result.estimatedValue?.toString() || "0");
  const [isEditing, setIsEditing] = useState(false);

  const handleConfirm = () => {
    const value = parseFloat(editedValue.replace(/,/g, ""));
    if (!isNaN(value)) {
      onConfirm(value, result.suggestedItemType);
    }
  };

  const formatCurrency = (value: string) => {
    const numValue = parseFloat(value.replace(/,/g, ""));
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString("en-US");
  };

  const { handleChange: handleValueChange, displayValue } = currencyInput(
    editedValue,
    setEditedValue,
  );

  return (
    <div className="border border-stone-200 rounded-lg p-6 mb-6 space-y-4 bg-white">
      <div>
        <div className="flex items-center gap-1.5">
          <h3 className="text-base font-medium text-stone-900">Estimated Value</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-stone-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                Elayne estimates are approximate and should not be considered professional
                appraisals.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-stone-500 mt-1">{result.suggestedItemName}</p>
      </div>

      <div className="space-y-3">
        {isEditing ? (
          <div className="space-y-2">
            <div className="relative">
              <span className="absolute left-3 top-[50%] -translate-y-[50%] text-stone-500 text-sm leading-none">
                $
              </span>
              <Input
                type="text"
                value={displayValue}
                onChange={handleValueChange}
                className="pl-7 text-base"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => setIsEditing(false)}
                variant="outline"
                className="flex-1"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <AnimatedShinyText className="text-2xl font-semibold text-stone-900">
              ${formatCurrency(editedValue)}
            </AnimatedShinyText>
            <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} className="h-8">
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleConfirm} className="flex-1">
          {assetType === "real-estate" ? "Confirm and add property" : "Confirm and add vehicle"}
        </Button>
        <Button onClick={onCancel} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
}
